import { Button } from 'antd';
import type { ButtonProps } from 'antd';
import { forwardRef } from 'react';
import {
  controlSizeForContext,
  CONTROL_CLASS,
} from '@/theme/controlSizing';
import type { ControlContext } from '@/theme/controlSizing';

/**
 * Props {@link ButtonBox} — wrapper Antd Button dengan context-aware size.
 */
export interface ButtonBoxProps extends Omit<ButtonProps, 'size'> {
  /**
   * Konteks pemakaian — menentukan density. Default: `form`.
   *
   * - `'page'` → `size="small"` (22px) — tombol di **filter halaman list**
   *   yang harus sejajar dengan SelectBox/DatePickerBox/TextInput compact.
   * - `'form'` → `size="middle"` (28px) — tombol di **form input modal**,
   *   dialog, header page, atau action area umum.
   * - `'drawer'` → `size="middle"` (alias `form`).
   */
  context?: ControlContext;
  /** Override paksa size Antd jika perlu (mis. tombol CTA besar pakai `large`). */
  size?: ButtonProps['size'];
}

/**
 * Button — wrapper Antd Button dengan context-aware sizing.
 *
 * **Kapan PAKAI ButtonBox vs Antd Button langsung?**
 * - **PAKAI ButtonBox** untuk tombol di filter halaman list (`context="page"`)
 *   atau tombol di form/modal/drawer biasa (`context="form"`).
 * - **PAKAI Antd Button langsung** untuk tombol toolbar header page (mis.
 *   "+ Input", "Export", "Refresh") yang memang ingin pakai default 28px
 *   tanpa context wrapper — atau untuk tombol di tempat khusus yang butuh
 *   `type="link"`/`shape="circle"`/dll. Tapi sebaiknya tetap pakai ButtonBox
 *   untuk konsistensi.
 *
 * **Mengapa context penting?**
 * Di filter halaman list, semua control (SelectBox/DatePickerBox/TextInput)
 * pakai `context="page"` → 22px (small density, sesuai filosofi compact).
 * Tombol filter (mis. Reset, Cari) WAJIB ikut 22px supaya sejajar — kalau
 * dibiarkan default Antd middle (28px), tombol akan terlihat "menonjol"
 * lebih tinggi dari filter sekelilingnya.
 *
 * Class `booku-control` dipasang untuk konsistensi (alignment via CSS).
 *
 * Pemakaian:
 * ```tsx
 * // Filter di halaman list (small/22px):
 * <ButtonBox context="page" icon={<ReloadOutlined />} onClick={onReset}>
 *   Reset
 * </ButtonBox>
 *
 * // Tombol di form modal (middle/28px — default):
 * <ButtonBox type="primary" onClick={handleSubmit}>
 *   Simpan
 * </ButtonBox>
 *
 * // Tombol icon-only:
 * <ButtonBox context="page" icon={<SearchOutlined />} />
 * ```
 *
 * Dokumentasi pattern: `.claude/docs/Booku v2/ui-patterns/control-system-pattern.md`
 */
export const ButtonBox = forwardRef<HTMLButtonElement, ButtonBoxProps>(
  function ButtonBox(
    { context = 'form', size, className, ...rest },
    ref
  ) {
    const resolvedSize = size ?? controlSizeForContext(context);
    return (
      <Button
        ref={ref}
        size={resolvedSize}
        className={`${CONTROL_CLASS}${className ? ` ${className}` : ''}`}
        {...rest}
      />
    );
  }
);
